/**
 * Sub-aba "Usuários" (a primeira, aberta por padrão): quem entra no painel e com que nível de
 * acesso. Porte de `admin.html:1930-1983` (tela) e `8811-8928` (comportamento); linhas 117 a 124
 * do inventário.
 *
 * O QUE A PESSOA FAZ AQUI, igual ao legado: lê a tabela fixa dos quatro níveis; vê a lista dos
 * usuários por ordem de cadastro, com "(você)" na própria linha; remove quem não é ela nem o último
 * Administrador; e adiciona um usuário com e-mail, senha provisória e nível, passando pela Edge
 * Function `criar-usuario-sistema`, com o segundo passo "conceder sem alterar a senha" quando a
 * conta já existe.
 */

// Estado, memória, referência e efeito do React.
import { useCallback, useEffect, useRef, useState } from 'react';
// As peças da plataforma: botão, campo e escolha.
import { Botao, Campo, Selecao } from '@complexity/ui';
// As chamadas da área, com a consulta do legado.
import {
  criarUsuarioDoPainel,
  listarUsuariosDoPainel,
  removerUsuarioDoPainel,
  type PedidoCriarUsuario,
} from '@studiolu/dados/areas/sistema';
// A linha de `usuarios_sistema`.
import type { LinhaUsuariosSistema } from '@studiolu/dados';
// Sessão, papel e fonte de dados.
import { usePainel } from '../../contexto.js';
// Os rótulos dos quatro papéis, palavra por palavra (`admin.html:8813`).
import { PAPEL_LABEL } from '../../dados/acesso.js';
// O parágrafo do status, desenhado como o legado.
import { LinhaDeStatus } from '../../comum/status-temporario.js';
// A memória da área, e o status que some em 3 s com o texto guardado nela (`mostrarStatusParamAdmin`).
import { useMemoria, useStatusDaArea } from './memoria.js';
// A linha compacta dos campos curtos, que a área divide com o Financeiro.
import { LinhaCompacta } from '../financeiro/apoio.js';
// As peças visuais da área.
import {
  BotaoDaLinha,
  CabecalhoDeColunas,
  CartaoParam,
  LinhaParam,
  NotaSecao,
  TextoParam,
  type Carga,
  type PropsDaSubAba,
} from './pecas.js';

/** Os quatro níveis e o que cada um pode fazer, texto por texto de `admin.html:1939-1954`. */
const NIVEIS: readonly { readonly nivel: string; readonly descricao: string }[] = [
  {
    nivel: 'Administrador',
    descricao:
      'Acesso total a todas as abas. O último Administrador do sistema não pode ser removido nem trocar de nível.',
  },
  {
    nivel: 'Financeiro',
    descricao:
      'Só vê Funil de Vendas e Financeiro. No Funil, só pode mover lead pra "Pagamento a receber" (o "Pagamento recebido" acontece sozinho ao quitar as parcelas). Não edita cadastro, produtos nem observações do lead. Não vê Lista de Clientes, Arquivos Administrativos nem Parâmetros do Sistema.',
  },
  {
    nivel: 'Marketing',
    descricao:
      'Só vê Funil de Vendas, em modo leitura, exceto origem e observações do lead, que pode editar. Não move estágio, não vincula cliente, não mexe em produtos. Não vê o resto do sistema.',
  },
  {
    nivel: 'Apenas Consulta',
    descricao:
      'Vê todas as abas (exceto Parâmetros do Sistema), mas não edita, exclui, envia nem move nada: só visualização.',
  },
];

// As opções do "Nível de acesso", na ordem do `<select>` do legado (`admin.html:1970-1973`).
const OPCOES_DE_NIVEL = ['administrador', 'financeiro', 'marketing', 'consulta'].map((valor) => ({
  valor,
  rotulo: PAPEL_LABEL[valor] ?? valor,
}));

export function SubAbaUsuarios({ confirmar, alertar, recarga }: PropsDaSubAba) {
  const { dados, acesso } = usePainel();
  // A lista começa carregando: o legado escreve "Carregando…" antes da consulta (`admin.html:8824`).
  const [lista, setLista] = useState<Carga<readonly LinhaUsuariosSistema[]>>({ tipo: 'carregando' });
  // A última lista lida com sucesso, que o "já está cadastrado" consulta (`administradoresCarregados`).
  const carregados = useRef<readonly LinhaUsuariosSistema[]>([]);
  // Os três campos do formulário; o nível começa no primeiro, como o `<select>` do legado. Na
  // memória da área: no legado eles são `input` fixos da página, e a ida a outra aba não os limpa.
  const [email, setEmail] = useMemoria('sistema:usuarios:email', '');
  const [senha, setSenha] = useMemoria('sistema:usuarios:senha', '');
  const [papel, setPapel] = useMemoria('sistema:usuarios:papel', 'administrador');
  // O parágrafo `#param-admin-status`, fora da lista no legado (`admin.html:1981`): também fica.
  const [status, mostrarStatus] = useStatusDaArea('sistema:usuarios:status');

  // Lê a lista por ordem de cadastro (`carregarAdministradores`, `admin.html:8822`).
  const carregar = useCallback(async () => {
    setLista({ tipo: 'carregando' });
    const resultado = await listarUsuariosDoPainel(dados);
    if (!resultado.ok) {
      // O erro de leitura escrito no lugar da lista, com a mensagem crua (`admin.html:8827`).
      setLista({ tipo: 'erro', mensagem: resultado.erro.mensagem });
      return;
    }
    carregados.current = resultado.dados ?? [];
    setLista({ tipo: 'pronta', dados: carregados.current });
  }, [dados]);

  // Lê ao abrir a área e a cada clique na sub-aba.
  useEffect(() => {
    void carregar();
  }, [carregar, recarga]);

  // "Remover": confirma, apaga, redesenha e avisa (`admin.html:8866-8876`).
  const remover = useCallback(
    async (alvo: string) => {
      if (!(await confirmar(`Remover "${alvo}" dos usuários? A pessoa perde o acesso ao painel imediatamente.`))) return;
      const resultado = await removerUsuarioDoPainel(dados, alvo);
      // A falha em aviso, com a mensagem crua depois do prefixo.
      if (!resultado.ok) {
        await alertar('Erro ao remover: ' + resultado.erro.mensagem);
        return;
      }
      // Tira a linha da lista em memória, sem reler o banco, como o `filter` do legado.
      carregados.current = carregados.current.filter((r) => r.email !== alvo);
      setLista({ tipo: 'pronta', dados: carregados.current });
      mostrarStatus('Usuário removido.');
    },
    [dados, confirmar, alertar, mostrarStatus],
  );

  // "Adicionar", passo a passo como `admin.html:8879-8927`.
  const adicionar = useCallback(async () => {
    // E-mail aparado e em minúsculas; a senha como foi digitada.
    const emailLimpo = email.trim().toLowerCase();
    const rotuloDoPapel = PAPEL_LABEL[papel] || papel;
    if (!emailLimpo || !emailLimpo.includes('@')) {
      mostrarStatus('Informe um e-mail válido.');
      return;
    }
    // 6 caracteres: o mínimo que o legado exige da senha provisória.
    if (!senha || senha.length < 6) {
      mostrarStatus('Informe uma senha de acesso com pelo menos 6 caracteres.');
      return;
    }
    if (carregados.current.some((r) => r.email === emailLimpo)) {
      mostrarStatus('Este e-mail já está cadastrado.');
      return;
    }
    if (!(await confirmar(`Adicionar "${emailLimpo}" como ${rotuloDoPapel}?`))) return;

    // O pedido do legado, com o token da sessão no cabeçalho (feito pela área de dados).
    const pedido: PedidoCriarUsuario = { email: emailLimpo, senha, papel };
    let resultado = await criarUsuarioDoPainel(dados, pedido);

    // A conta já existe: o segundo passo, conceder o nível sem trocar a senha (`admin.html:8903`).
    //
    // A MENSAGEM VAI EM UM PARÁGRAFO SÓ, e é assim que o legado a DESENHA: o texto dele tem "\n\n"
    // (`admin.html:8906`), mas o `.modal-subtitulo` não tem `white-space` (`admin.html:1378`) e
    // nenhuma regra `pre-*` o alcança, então as duas quebras viram um espaço e as frases saem
    // corridas, em 39 px de altura. Medido na verificação de 19/09/2026 (divergência D4), com o CSS
    // do legado. Escrever "\n\n" aqui abriria uma linha em branco que o legado não tem.
    if (resultado.ok && resultado.dados?.ok === false && resultado.dados?.codigo === 'conta_existente') {
      const querConceder = await confirmar(
        `Este e-mail já tem login no sistema. Conceder o acesso de ${rotuloDoPapel} sem alterar a senha dela?`,
      );
      if (!querConceder) {
        mostrarStatus('Cadastro cancelado.');
        return;
      }
      resultado = await criarUsuarioDoPainel(dados, { ...pedido, conceder_sem_senha: true });
    }

    // Recusa de regra de negócio (`ok: false` com status 200): o texto da função (`admin.html:8918`).
    if (resultado.ok && resultado.dados?.ok === false) {
      mostrarStatus(resultado.dados.error || 'Não foi possível concluir. Nada foi alterado.');
      return;
    }
    // Falha da chamada, ou `error` no corpo: o motivo depois do prefixo (`admin.html:8923`).
    if (!resultado.ok || resultado.dados?.error) {
      const motivo = resultado.ok ? resultado.dados?.error : resultado.erro.mensagem;
      mostrarStatus('Erro ao adicionar: ' + motivo);
      return;
    }
    // Deu certo: limpa e-mail e senha (o nível fica), avisa e relê a lista.
    setEmail('');
    setSenha('');
    mostrarStatus(resultado.dados?.aviso || 'Usuário adicionado.');
    await carregar();
  }, [email, senha, papel, dados, confirmar, mostrarStatus, carregar, setEmail, setSenha]);

  return (
    <div className="flex flex-col gap-cx-4">
      <NotaSecao>
        Cada usuário tem um nível de acesso. Veja o que cada um pode fazer na tabela abaixo antes de escolher.
      </NotaSecao>

      {/* A tabela fixa dos quatro níveis (`admin.html:1933-1956`). */}
      <CartaoParam testid="tabela-niveis">
        <CabecalhoDeColunas>
          <span className="md:w-[148px] md:shrink-0">Nível</span>
          <span className="min-w-0 flex-1">O que pode fazer</span>
        </CabecalhoDeColunas>
        {NIVEIS.map((linha) => (
          <LinhaParam key={linha.nivel}>
            <span className="basis-full text-cx-sm md:w-[148px] md:shrink-0 md:basis-auto">
              <strong>{linha.nivel}</strong>
            </span>
            <span className="min-w-0 basis-full text-cx-sm text-texto-suave [overflow-wrap:anywhere] md:flex-1 md:basis-0">
              {linha.descricao}
            </span>
          </LinhaParam>
        ))}
      </CartaoParam>

      {/* O formulário em linha (`.param-adicionar`, `admin.html:1958-1979`). */}
      <LinhaCompacta>
        <Campo
          rotulo="E-mail"
          type="email"
          name="param-admin-novo-email"
          autoComplete="email"
          placeholder="nome@exemplo.com"
          value={email}
          onChange={(evento) => setEmail(evento.target.value)}
        />
        {/* Campo de TEXTO visível, como o legado: é a senha provisória que a administradora dita. */}
        <Campo
          rotulo="Senha de acesso"
          type="text"
          name="param-admin-nova-senha"
          autoComplete="new-password"
          placeholder="senha provisória"
          value={senha}
          onChange={(evento) => setSenha(evento.target.value)}
        />
        <Selecao
          rotulo="Nível de acesso"
          opcoes={OPCOES_DE_NIVEL}
          value={papel}
          onChange={(evento) => setPapel(evento.target.value)}
        />
        <Botao onClick={() => void adicionar()}>Adicionar</Botao>
      </LinhaCompacta>
      <p className="text-cx-xs text-texto-suave">
        A senha aqui cria o login de verdade pra essa pessoa (e-mail + senha). Se ela já tiver conta (ex: já era cliente ou
        entra com Google), só o nível de acesso é concedido, a senha dela não muda.
      </p>
      <LinhaDeStatus texto={status} testid="status-usuarios" />

      {/* A lista (`#param-administradores-wrap`). */}
      <ListaDeUsuarios lista={lista} emailAtual={acesso.email} aoRemover={(alvo) => void remover(alvo)} />
    </div>
  );
}

/** A lista dos usuários, com as regras de quem pode ser removido (`admin.html:8834-8864`). */
function ListaDeUsuarios({
  lista,
  emailAtual,
  aoRemover,
}: {
  readonly lista: Carga<readonly LinhaUsuariosSistema[]>;
  readonly emailAtual: string;
  readonly aoRemover: (email: string) => void;
}) {
  if (lista.tipo === 'carregando') return <TextoParam>Carregando…</TextoParam>;
  if (lista.tipo === 'erro') return <TextoParam>{`Erro ao carregar: ${lista.mensagem}`}</TextoParam>;
  if (lista.dados.length === 0) return <TextoParam>Nenhum usuário cadastrado.</TextoParam>;
  // Quantos Administradores há: com um só, ele não pode ser removido.
  const totalAdministradores = lista.dados.filter((r) => r.papel === 'administrador').length;
  return (
    <CartaoParam testid="lista-usuarios">
      <CabecalhoDeColunas>
        <span className="min-w-0 flex-1">E-mail</span>
        <span className="min-w-0 flex-1">Cadastrado</span>
        <span className="min-w-0 flex-1">Nível de acesso</span>
        <span className="w-[76px] shrink-0" />
      </CabecalhoDeColunas>
      {lista.dados.map((linha) => {
        // O último Administrador e a própria pessoa não têm "Remover" (`admin.html:8850-8858`).
        const ultimoAdmin = linha.papel === 'administrador' && totalAdministradores <= 1;
        const voceMesmo = linha.email === emailAtual;
        return (
          <LinhaParam key={linha.email} data-email={linha.email}>
            <span className="min-w-0 basis-full text-cx-sm [overflow-wrap:anywhere] md:flex-1 md:basis-0">
              {linha.email}
              {voceMesmo && <em className="not-italic text-texto-fraco"> (você)</em>}
            </span>
            <span className="min-w-0 basis-full text-cx-sm [overflow-wrap:anywhere] md:flex-1 md:basis-0">
              {linha.criado_por ? `por ${linha.criado_por}` : <span className="text-texto-suave">não informado</span>}
            </span>
            <span className="min-w-0 basis-full text-cx-sm md:flex-1 md:basis-0">{PAPEL_LABEL[linha.papel] || linha.papel}</span>
            <span className="ml-auto shrink-0 text-right md:w-[76px]">
              {!ultimoAdmin && !voceMesmo && <BotaoDaLinha perigo onClick={() => aoRemover(linha.email)}>Remover</BotaoDaLinha>}
            </span>
          </LinhaParam>
        );
      })}
      {/* O aviso do último Administrador, dentro do cartão, depois das linhas (`admin.html:8863`). */}
      {totalAdministradores <= 1 && (
        <p className="px-cx-4 py-cx-2 text-cx-sm text-texto-suave">
          O sistema precisa ter sempre uma pessoa com acesso de Administrador. Como esta é a única hoje, ela não pode ser
          removida nem trocar de nível. Para isso, cadastre outro Administrador antes.
        </p>
      )}
    </CartaoParam>
  );
}
